import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, MapPin, Upload } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { GalleryImage } from '../../types/gallery';
import GalleryModal from './GalleryModal';
import BatchImportModal from './BatchImportModal';

const GalleryList = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBatchImportOpen, setIsBatchImportOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const { data, error } = await supabase
        .from('gallery_images')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Fetch tour information separately for images that have tour_id
      const imagesWithTours = await Promise.all(
        (data || []).map(async (image) => {
          if (image.tour_id) {
            const { data: tourData } = await supabase
              .from('tours')
              .select('id, name, slug')
              .eq('id', image.tour_id)
              .single();
            
            return { ...image, tour: tourData };
          }
          return image;
        })
      );
      
      setImages(imagesWithTours);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Failed to fetch images');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const { error } = await supabase
        .from('gallery_images')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setImages(images.filter(img => img.id !== id));
    } catch (err) {
      console.error('Error deleting image:', err);
      alert('Failed to delete image');
    }
  };

  const handleEdit = (image: GalleryImage) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedImage(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
    fetchImages();
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex gap-3">
          <button
            onClick={() => setIsBatchImportOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#2A382A] text-white rounded-lg hover:bg-[#1F2B1F] transition-colors"
          >
            <Upload className="w-4 h-4" />
            Batch Import
          </button>
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Image
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {images.map((image) => (
          <div 
            key={image.id}
            className="bg-[#1F2B1F] rounded-xl overflow-hidden group"
          >
            <div className="aspect-video relative">
              <img
                src={image.image_url}
                alt={image.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                <button
                  onClick={() => handleEdit(image)}
                  className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <Pencil className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(image.id)}
                  className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors text-red-500"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-semibold mb-1">{image.title}</h3>
              <p className="text-sm text-white/70">{image.description}</p>
              {image.tour && (
                <div className="flex items-center gap-1 mt-2">
                  <MapPin className="w-3 h-3 text-[#E85C0D]" />
                  <span className="text-xs text-[#E85C0D] font-medium">
                    {image.tour.name}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <GalleryModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        image={selectedImage}
      />

      <BatchImportModal
        isOpen={isBatchImportOpen}
        onClose={() => setIsBatchImportOpen(false)}
        onImportComplete={fetchImages}
      />
    </div>
  );
};

export default GalleryList;